import { PauseIcon, PlayIcon } from '@heroicons/react/24/outline';
import { VoiceType } from '../../api/voice';
import { useAudioPlayer } from '../../hooks/index/use-audio-player';
import { Backward10 } from '../exam-review/voices/icons/backward10';
import { Forward10 } from '../exam-review/voices/icons/forward10';

const SKIP_SECONDS = 10;

const formatTime = (time: number) => {
  if (!Number.isFinite(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

export const VoicePlayer = ({ voice }: { voice: VoiceType }) => {
  const {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    progress,
    speed,
    togglePlay,
    skip,
    seekToPercent,
    cycleSpeed,
    audioEventHandlers
  } = useAudioPlayer();

  return (
    <div
      className='flex w-full flex-col items-center justify-between
                 gap-1.5 px-1 py-1 sm:flex-row sm:gap-4 sm:py-0.5'
    >
      <audio
        ref={audioRef}
        src={process.env.REACT_APP_MEDIA_BASE_URL + voice.file}
        className='hidden'
        {...audioEventHandlers}
      />

      <div className='flex w-full min-w-0 flex-col justify-center gap-1 sm:flex-1 sm:gap-2'>
        <div className='flex w-full items-center justify-between'>
          <div className='flex items-center gap-2 overflow-hidden pr-1'>
            <span
              className='truncate text-[13px] font-bold text-zinc-800 sm:text-sm'
              title={voice.name}
            >
              {voice.name}
            </span>
          </div>

          <div className='flex items-center gap-2'>
            <span
              dir='ltr'
              className='whitespace-nowrap text-[10px] font-semibold text-zinc-500 sm:text-[11px]'
            >
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            <button
              type='button'
              onClick={cycleSpeed}
              aria-label={`تغییر سرعت پخش، فعلی ${speed}x`}
              className='whitespace-nowrap rounded-md bg-sky-50/80
                         px-2 py-0.5 text-[10px] font-bold text-sky-600 transition-colors
                         hover:bg-sky-100 active:scale-95'
            >
              {speed}x
            </button>
          </div>
        </div>

        <div className='group relative flex h-4 w-full cursor-pointer items-center' dir='ltr'>
          <input
            type='range'
            min='0'
            max='100'
            value={progress}
            onChange={e => seekToPercent(Number(e.target.value))}
            aria-label='پیشرفت پخش'
            className='absolute z-20 h-full w-full cursor-pointer opacity-0'
          />
          <div
            className='relative flex h-1.5 w-full items-center
             overflow-hidden rounded-full bg-zinc-200/80'
          >
            <div
              className='absolute left-0 h-full rounded-full
               bg-gradient-to-r from-sky-400 to-sky-600
               transition-all duration-75 ease-linear'
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      <div
        className='mt-0.5 flex w-[85%] flex-shrink-0 items-center
                   justify-center gap-2 self-center
                   rounded-full border border-sky-100/40 bg-white/40 px-4
                   py-1 shadow-sm backdrop-blur-md sm:mt-0
                   sm:w-auto sm:gap-1 sm:self-auto sm:px-0.5 sm:py-0.5'
        dir='ltr'
      >
        <button
          type='button'
          onClick={() => skip(-SKIP_SECONDS)}
          aria-label='ده ثانیه به عقب'
          className='rounded-full p-1.5 text-zinc-500 transition-all
                     hover:text-sky-600 active:scale-90'
        >
          <Backward10 />
        </button>

        <button
          type='button'
          onClick={togglePlay}
          aria-label={isPlaying ? 'مکث' : 'پخش'}
          className='flex h-10 w-10 flex-shrink-0 items-center
                     justify-center rounded-full bg-gradient-to-br from-sky-500 to-sky-700
                     text-white shadow-md shadow-sky-500/30 transition-all duration-300
                     hover:from-sky-600 hover:to-sky-800 active:scale-95 sm:h-9 sm:w-9'
        >
          {isPlaying ? (
            <PauseIcon className='h-5 w-5' />
          ) : (
            <PlayIcon className='h-5 w-5 translate-x-[1px]' />
          )}
        </button>

        <button
          type='button'
          onClick={() => skip(SKIP_SECONDS)}
          aria-label='ده ثانیه به جلو'
          className='rounded-full p-1.5 text-zinc-500 transition-all
                     hover:text-sky-600 active:scale-90'
        >
          <Forward10 />
        </button>
      </div>
    </div>
  );
};
